import {
  Document,
  Font,
  Image,
  Page,
  StyleSheet,
  Text,
  View,
} from "@react-pdf/renderer";

import config from "../../config/api.config";
import { splitDateTime } from "../../services/DateTimeService";
import {
  DocumentationSliceState,
  Technician,
} from "../../store/documentation-slice";
import { Comment } from "../../types/comment";
import { MinimalProduct } from "../../types/product";

import delexLogo from "./delexLogo.png";
import fmLogo from "./fm-logo.png";
import { deficiencyTypeMappings } from "./PDFDocumentForm";

Font.register({
  family: "Roboto",
  fonts: [
    {
      src: require("@fontsource/roboto/files/roboto-latin-400-normal.woff"), // Regular
      fontWeight: "normal",
    },
    {
      src: require("@fontsource/roboto/files/roboto-latin-500-normal.woff"), // Bold
      fontWeight: "medium",
    },
    {
      src: require("@fontsource/roboto/files/roboto-latin-400-italic.woff"), // Italic
      fontStyle: "italic",
      fontWeight: "normal",
    },
  ],
});

const styles = StyleSheet.create({
  page: {
    flexDirection: "column",
    padding: 20,
    paddingBottom: 80,
    fontFamily: "Roboto",
  },
  normalPage: {
    flexDirection: "column",
    padding: 20,
    paddingBottom: 80,
    fontFamily: "Roboto",
  },
  title: {
    fontSize: 7,
    flexDirection: "column",
    display: "flex",
    marginBottom: 4,
    color: "#8493AB",
  },
  floorName: {
    flexDirection: "column",
    display: "flex",
    marginBottom: 4,
    color: "#3d5170",
    fontWeight: "medium",
    fontSize: 14,
  },
  name: {
    flexDirection: "column",
    display: "flex",
    marginBottom: 4,
    color: "#3d5170",
    fontWeight: "medium",
    fontSize: 11,
  },
  signatureName: {
    color: "#3d5170",
    fontWeight: "medium",
    fontSize: 14,
  },
  deficiencyName: {
    flexDirection: "column",
    display: "flex",
    marginBottom: 22,
    color: "#3d5170",
    fontWeight: "medium",
    fontSize: 14,
  },
  documentName: {
    color: "#3d5170",
    fontWeight: "medium",
    fontSize: 18,
    marginTop: 96,
    marginBottom: 24,
  },
  text: {
    flexDirection: "column",
    display: "flex",
    color: "#3d5170",
    fontSize: 11,
    minWidth: 30,
    fontWeight: "medium",
  },
  address: {
    marginBottom: 4,
    color: "#3d5170",
    fontSize: 9,
    minWidth: 30,
    flexWrap: "wrap",
    wordWrap: "break-word",
    overflowWrap: "break-word",
  },
  tableTitle: {
    fontSize: 7,
    flexDirection: "column",
    display: "flex",
    minWidth: 90,
    marginBottom: 5,
    color: "#8493AB",
  },
  tableText: {
    flexDirection: "column",
    display: "flex",
    minWidth: 90,
    marginBottom: 5,
    color: "#3d5170",
    fontSize: 9,
  },
  tableTitleProduct: {
    fontSize: 7,
    flexDirection: "column",
    display: "flex",
    marginBottom: 5,
    color: "#8493AB",
  },
  monitorType: {
    color: "#3d5170",
    fontSize: 9,
  },
  findings: {
    fontSize: 11,
    color: "#3d5170",
    marginTop: 22,
    marginBottom: 6,
    fontWeight: "medium",
  },
  tableTextProduct: {
    flexDirection: "column",
    display: "flex",
    marginBottom: 5,
    color: "#3d5170",
    fontSize: 9,
  },
  tableTextReason: {
    fontWeight: "normal",
    fontStyle: "italic",
    flexDirection: "column",
    display: "flex",
    minWidth: 90,
    marginTop: 5,
    marginBottom: 10,
    color: "#8493AB",
    fontSize: 9,
  },
  information: {
    flexDirection: "row",
    display: "flex",
    justifyContent: "space-between",
  },
  remarkBox: {
    marginTop: 8,
    width: "auto",
  },
  section: {
    margin: 10,
    padding: 10,
    flexDirection: "column",
    flexGrow: 1,
  },
  recommendation: {
    fontSize: 9,
    color: "#3d5170",
  },
  comment: {
    fontSize: 9,
    color: "#3d5170",
    marginBottom: 4,
  },
  commentName: {
    fontSize: 9,
    color: "#3d5170",
  },
  bottomSection: {
    marginBottom: 10,
    marginLeft: 10,
    marginRight: 10,
    padding: 10,
    marginTop: "auto",
  },
  products: {
    marginTop: 10,
    width: "100%",
  },
  productsRow: {
    flexDirection: "row",
    paddingBottom: 2,
  },
  qty: {
    width: 60,
  },
  pName: {
    flexShrink: 1,
    flexGrow: 0,
    width: 230,
  },
  description: {
    flex: 1,
    flexWrap: "wrap",
  },
  cardContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    display: "flex",
    gap: 10,
  },
  container2: {
    marginTop: 22,
  },
  container3: {
    marginBottom: 22,
  },
  container: {
    marginBottom: 22,
    marginTop: 6,
  },
  card: {
    width: "calc(25% - 20px)",
    flexDirection: "column",
    display: "flex",
    gap: 5,
  },
  tableHeader: {
    flexDirection: "row",
    display: "flex",
    gap: 10,
    marginTop: 3,
    justifyContent: "flex-start",
    borderBottomWidth: 0.4,
    borderBottomColor: "#8493AB",
  },
  tableRow: {
    flexDirection: "row",
    display: "flex",
    gap: 10,
    justifyContent: "flex-start",
    borderBottomWidth: 0.4,
    borderBottomColor: "#8493AB",
  },
  tableRowMonitor: {
    flexDirection: "row",
    display: "flex",
    gap: 10,
    justifyContent: "flex-start",
  },
  signatureContainer: {
    width: "50%",
    height: 100,
    backgroundColor: "#EDEFF5",
    borderRadius: 10,
    padding: 10,
  },
  signaturePhoto: {
    width: "100%",
  },
  firmaText: {
    padding: 15,
    justifyContent: "center",
    fontSize: 8,
    width: "50%",
    marginBottom: 5,
    color: "#8493AB",
  },
  lastPart: {
    flexDirection: "row",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    marginTop: 22,
  },
  thickLine: {
    height: 4,
    backgroundColor: "#CED3DC",
    borderRadius: 2,
    width: "100%",
    marginBottom: 24,
    marginTop: 24,
  },
  thickLine2: {
    height: 4,
    backgroundColor: "#CED3DC",
    borderRadius: 2,
    marginTop: 22,
    marginBottom: 22,
    width: "100%",
  },
  footer: {
    position: "absolute",
    bottom: 20,
    left: 20,
    right: 20,
    height: 50,
    flexDirection: "row",
    display: "flex",
    justifyContent: "space-between",
    fontSize: 7,
    color: "#3d5170",
    paddingRight: 20,
    paddingLeft: 20,
  },
  nameContainer: {
    position: "absolute",
    bottom: 0,
    left: 10,
    right: 10,
  },
  productsContainer: {
    marginTop: 24,
    marginBottom: 24,
  },
  line: {
    borderBottomWidth: 1,
    borderBottomColor: "#8493AB",
    width: "100%",
    marginTop: 5,
    marginBottom: 5,
  },
  footerTitle: {
    fontSize: 9,
    fontWeight: "medium",
    marginBottom: 4,
  },
  footerItem: {
    flexGrow: 1,
  },
  thirdItem: {
    flex: 1,
    textAlign: "left",
  },
  signature: {
    marginTop: 24,
  },
  fullPageImage: {
    width: "100%",
    height: "100%",
  },
  imageContainer: {
    justifyContent: "flex-start",
    alignItems: "flex-end",
    width: "100%",
    height: 70,
  },
  image: {
    width: 100,
    height: "100%",
    objectFit: "contain",
  },
  pNameWrapper: {
    flexDirection: "column",
  },
  payExtraText: {
    fontSize: 7,
    color: "#5C7499",
    marginBottom: 3,
  },
});

type DocumentationProps = {
  documentation: DocumentationSliceState;
};

const Footer = () => (
  <View style={styles.footer} fixed>
    <View style={styles.footerItem}>
      <Text style={styles.footerTitle}>{config.companyName}</Text>
      <Text style={styles.title}>{config.companyAddress}</Text>
      <Text style={styles.title}>{config.companyCity}</Text>
    </View>

    <View style={[styles.footerItem, styles.thirdItem]}>
      <Text style={styles.title}>Seite</Text>
      <Text
        style={styles.footerTitle}
        render={({ pageNumber, totalPages }) =>
          `${pageNumber} von ${totalPages}`
        }
        fixed
      />
    </View>
  </View>
);
export const DeficiencyPDFDocumentForm: React.FC<DocumentationProps> = ({
  documentation,
}) => {
  const shouldShowComments =
    documentation.showCommentsInPDF &&
    documentation.deficiencyPDF.commentsList.length > 0;

  const commentsBefore = documentation.deficiencyPDF.commentsList.filter(
    (comment) =>
      documentation.deficiencyPDF.processedAt &&
      new Date(comment.createdAt) <=
        new Date(documentation.deficiencyPDF.processedAt)
  );
  const commentsAfter = documentation.deficiencyPDF.commentsList.filter(
    (comment) =>
      documentation.deficiencyPDF.processedAt &&
      new Date(comment.createdAt) >
        new Date(documentation.deficiencyPDF.processedAt)
  );

  const { date, time } = splitDateTime(
    documentation.deficiencyPDF.processedAt,
    true
  );

  const logoUrl =
    config.companyName === "F+M Schädlingsbekämpfungs GmbH"
      ? fmLogo
      : delexLogo;

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.section}>
          <View style={styles.imageContainer}>
            <Image src={logoUrl} style={styles.image} />
          </View>
          <View style={styles.information}>
            <View style={{ flex: 1, flexDirection: "column" }}>
              <Text style={styles.title}>Kunde</Text>
              <Text style={styles.name}>
                {documentation.appointmentSubset.customerName}
              </Text>
              {documentation.appointmentSubset.objectName && (
                <Text style={styles.address}>
                  {documentation.appointmentSubset.objectName}
                </Text>
              )}
              <Text style={styles.address}>
                {documentation.appointmentSubset.street}
              </Text>
              <Text style={styles.address}>
                {documentation.appointmentSubset.postcode}{" "}
                {documentation.appointmentSubset.city}
              </Text>
            </View>
          </View>

          <Text style={styles.documentName}>
            {documentation.deficiencyPDF?.state === "open"
              ? "Mangelerfassung"
              : "Zusammenfassung des Mangels"}
          </Text>

          <View style={styles.information}>
            <View>
              <Text style={styles.title}>Art des Mangels</Text>
              {documentation.deficiencyPDF?.deficiencyType && (
                <Text style={styles.text}>
                  {
                    deficiencyTypeMappings[
                      documentation.deficiencyPDF?.deficiencyType
                    ]
                  }
                </Text>
              )}

              {documentation.totalTimeWorkload !== "" && (
                <View style={{ marginTop: 10 }}>
                  <Text style={styles.title}>Arbeitsaufwand</Text>
                  <Text style={styles.text}>
                    {documentation.totalTimeWorkload}
                  </Text>
                </View>
              )}
            </View>

            <View>
              <Text style={styles.title}>Erfasst am</Text>
              <Text style={styles.text}>
                {documentation.deficiencyPDF.date}
              </Text>

              {documentation.appointmentSubset.technicians.length > 0 && (
                <View style={{ marginTop: 10 }}>
                  <Text style={styles.title}>Verantwortlicher</Text>
                  {documentation.appointmentSubset.technicians.map(
                    (technician: Technician, index: number) => (
                      <View key={index}>
                        <Text style={styles.text}>
                          {technician.firstName && technician.lastName
                            ? `${technician.firstName} ${technician.lastName}`
                            : technician.profileName}
                        </Text>
                      </View>
                    )
                  )}
                </View>
              )}
            </View>

            <View>
              <Text style={styles.title}>Standort</Text>
              <Text style={[styles.text, { maxWidth: "30%" }]}>
                {documentation.deficiencyPDF.location}
              </Text>
            </View>

            <View>
              <Text style={styles.title}>Verantwortung</Text>
              <Text style={styles.text}>
                {documentation.deficiencyPDF?.responsibility === "customer"
                  ? "Kunde"
                  : "SBK"}
              </Text>
            </View>
          </View>

          <View style={styles.thickLine} />

          {documentation.deficiencyPDF.requiredActions !== undefined && (
            <View>
              <Text style={styles.name}>Empfehlung</Text>

              <View style={styles.remarkBox}>
                <Text style={styles.recommendation}>
                  {documentation.deficiencyPDF.requiredActions.join(", ")}
                </Text>
              </View>

              <View style={styles.thickLine} />
            </View>
          )}

          {documentation.deficiencyPDF.additionalText && (
            <View>
              <Text style={styles.name}>Zusätzlicher Text</Text>

              <View style={styles.remarkBox}>
                <Text style={styles.recommendation}>
                  {documentation.deficiencyPDF.additionalText}
                </Text>
              </View>

              <View style={styles.thickLine} />
            </View>
          )}

          {documentation.products.length > 0 && (
            <View style={styles.productsContainer}>
              <Text style={styles.name}>Materialeinsatz</Text>
              <View style={styles.products}>
                <View style={styles.productsRow}>
                  <Text style={[styles.tableTitleProduct, styles.qty]}>
                    Menge
                  </Text>
                  <Text style={[styles.tableTitleProduct, styles.pName]}>
                    Artikel
                  </Text>
                  <Text style={[styles.tableTitleProduct, styles.description]}>
                    Beschreibung
                  </Text>
                </View>
                {documentation.products.map((product: MinimalProduct) => (
                  <View
                    key={product.id}
                    style={[
                      styles.productsRow,
                      {
                        borderTopWidth: 0.4,
                        borderTopColor: "#CED3DC",
                        paddingTop: 2,
                      },
                    ]}
                  >
                    <Text style={[styles.tableTextProduct, styles.qty]}>
                      {product.quantity}
                    </Text>
                    <View style={styles.pNameWrapper}>
                      <Text style={[styles.tableTextProduct, styles.pName]}>
                        {product.name}
                      </Text>

                      {(product.payExtraQuantity ?? 0) > 0 && (
                        <Text style={styles.payExtraText}>
                          {product.payExtraQuantity} von {product.quantity}{" "}
                          wurden als kostenpflichtig markiert
                        </Text>
                      )}
                    </View>
                    <Text style={[styles.tableTextProduct, styles.description]}>
                      {product.description ? product.description : "-"}
                    </Text>
                  </View>
                ))}
              </View>
            </View>
          )}

          {Array.isArray(documentation.deficiencyPDF.actions) && (
            <View>
              <Text style={styles.name}>Maßnahmen</Text>

              <View style={styles.remarkBox}>
                <Text style={styles.recommendation}>
                  {documentation.deficiencyPDF.actions.join(", ")}
                </Text>
              </View>

              <View style={styles.thickLine} />
            </View>
          )}

          {shouldShowComments && (
            <View wrap>
              <Text style={styles.name}>Kommentare</Text>
              {documentation.deficiencyPDF.processedAt &&
              documentation.deficiencyPDF.processedBy ? (
                <View style={{ marginTop: 10 }}>
                  {commentsBefore.map((comment, index) => (
                    <View key={index} style={{ marginBottom: 10 }}>
                      {comment.isSystem === 1 && comment.additionalData ? (
                        <Text style={styles.commentName}>
                          {`${comment.additionalData.from.name || "N/A"}`}{" "}
                          teilte den Mangel mit{" "}
                          <Text style={{ fontWeight: "bold" }}>
                            {comment.additionalData.to.email}
                          </Text>{" "}
                          {`[${
                            splitDateTime(
                              comment.additionalData.timestamp,
                              true
                            ).date
                          }, ${
                            splitDateTime(
                              comment.additionalData.timestamp,
                              true
                            ).time
                          }]`}
                        </Text>
                      ) : (
                        <>
                          <Text style={styles.commentName}>
                            {`${comment.createdBy?.name} [`}
                            {`${splitDateTime(comment.createdAt, true).date}, ${
                              splitDateTime(comment.createdAt, true).time
                            }]:`}
                          </Text>
                          <Text style={styles.comment}>{comment.content}</Text>
                        </>
                      )}
                    </View>
                  ))}

                  <View>
                    <Text
                      style={{
                        fontWeight: "bold",
                        fontSize: 9,
                        color: "#3D5170",
                      }}
                    >
                      Mangel abgeschlossen von{" "}
                      {documentation.deficiencyPDF.processedBy?.user || "N/A"} [
                      {date}, {time}]
                    </Text>
                  </View>

                  {commentsAfter.map((comment, index) => (
                    <View key={index} style={{ marginBottom: 10 }}>
                      {comment.isSystem === 1 && comment.additionalData ? (
                        <Text style={styles.commentName}>
                          {`${comment.additionalData.from.name || "N/A"}`}{" "}
                          teilte den Mangel mit{" "}
                          <Text style={{ fontWeight: "bold" }}>
                            {comment.additionalData.to.email}
                          </Text>{" "}
                          {`[${
                            splitDateTime(
                              comment.additionalData.timestamp,
                              true
                            ).date
                          }, ${
                            splitDateTime(
                              comment.additionalData.timestamp,
                              true
                            ).time
                          }]`}
                        </Text>
                      ) : (
                        <>
                          <Text style={styles.commentName}>
                            {`${comment.createdBy?.name} [`}
                            {`${splitDateTime(comment.createdAt, true).date}, ${
                              splitDateTime(comment.createdAt, true).time
                            }]:`}
                          </Text>
                          <Text style={styles.comment}>{comment.content}</Text>
                        </>
                      )}
                    </View>
                  ))}
                </View>
              ) : (
                <View style={{ marginTop: 10 }}>
                  {documentation.deficiencyPDF.commentsList.map(
                    (comment: Comment, index: number) => (
                      <View key={index} style={{ marginBottom: 10 }}>
                        {comment.isSystem === 1 && comment.additionalData ? (
                          <Text style={styles.commentName}>
                            {`${comment.additionalData.from.name || "N/A"}`}{" "}
                            teilte den Mangel mit{" "}
                            <Text style={{ fontWeight: "bold" }}>
                              {comment.additionalData.to.email}
                            </Text>{" "}
                            {`[${
                              splitDateTime(
                                comment.additionalData.timestamp,
                                true
                              ).date
                            }, ${
                              splitDateTime(
                                comment.additionalData.timestamp,
                                true
                              ).time
                            }]`}
                          </Text>
                        ) : (
                          <>
                            <Text style={styles.commentName}>
                              {`${comment.createdBy?.name} [`}
                              {`${
                                splitDateTime(comment.createdAt, true).date
                              }, ${
                                splitDateTime(comment.createdAt, true).time
                              }]:`}
                            </Text>
                            <Text style={styles.comment}>
                              {comment.content}
                            </Text>
                          </>
                        )}
                      </View>
                    )
                  )}
                </View>
              )}
            </View>
          )}
        </View>
        <View style={styles.bottomSection}>
          {documentation.signature?.url && (
            <View>
              <Text style={styles.signatureName}>Unterschrift</Text>
              <View style={styles.lastPart}>
                <View style={styles.signatureContainer}>
                  <Image
                    src={documentation.signature?.url}
                    style={styles.signaturePhoto}
                  />
                  <View style={styles.nameContainer}>
                    <View style={styles.line} />
                    <Text style={styles.title}>{documentation.emailOwner}</Text>
                  </View>
                </View>
                <Text style={styles.firmaText}>
                  Die Firma {config.companyName} ist durch den o. g.
                  Auftraggeber beauftragt, in o. g. Lieferanschrift die
                  dokumentierten Arbeiten durchzuführen und/oder Präparate
                  einzusetzen. Achtung! Über Gefährdungen- Sicherheits- und
                  Reinigungsmaßnahmen, sowie die allgemeinen Hinweise wurde der
                  Auftraggeber vor Beginn der Maßnahme aufgeklärt.
                </Text>
              </View>
            </View>
          )}
        </View>
        <Footer />
      </Page>
    </Document>
  );
};
